import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Eye, Inbox, Pencil, RefreshCw, Search, Trash2 } from "lucide-react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { isInvoiceReturnsEnabled } from "@/features/invoiceReturnsFeature";
import { invoiceReturnsApi, type InvoiceReturn } from "@/lib/api";
import { formatIntegratedOrderDate, integratedOrderErrorMessage, integratedOrderPageCount } from "@/lib/integratedOrders";
import {
  filterInvoiceReturns,
  INVOICE_RETURN_KIND_OPTIONS,
  INVOICE_RETURN_RESULT_OPTIONS,
  invoiceReturnDetail,
  invoiceReturnKindLabel,
  invoiceReturnReasonLabel,
  summarizeInvoiceReturns,
  type InvoiceReturnKindFilter,
  type InvoiceReturnResultFilter,
} from "@/lib/invoiceReturns";
import { InvoiceReturnDetailsDialog } from "./InvoiceReturnDetailsDialog";
import { InvoiceReturnEditSheet } from "./InvoiceReturnEditSheet";

const PAGE_SIZE_OPTIONS = [25, 50, 100];
// O acompanhamento atualiza sozinho: a tela é aberta só para ver se o retorno do ERP chegou.
const REFRESH_INTERVAL_MS = 30_000;

export default function InvoiceReturns() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState<InvoiceReturnKindFilter>("all");
  const [result, setResult] = useState<InvoiceReturnResultFilter>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [details, setDetails] = useState<InvoiceReturn | null>(null);
  const [editing, setEditing] = useState<InvoiceReturn | null>(null);
  const [toDiscard, setToDiscard] = useState<InvoiceReturn | null>(null);

  // Qualquer mudança de filtro volta para a primeira página.
  useEffect(() => {
    setPage(1);
  }, [search, kind, result, pageSize]);

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ["invoice-returns"],
    queryFn: () => invoiceReturnsApi.list(),
    refetchInterval: REFRESH_INTERVAL_MS,
    enabled: isInvoiceReturnsEnabled,
  });
  const all = useMemo<InvoiceReturn[]>(() => data ?? [], [data]);
  const summary = useMemo(() => summarizeInvoiceReturns(all), [all]);
  const filtered = useMemo(() => filterInvoiceReturns(all, { search, kind, result }), [all, search, kind, result]);

  // Reprocessar reaplica o corpo guardado pelo mesmo serviço do ERP (idempotente): não precisa de confirmação.
  const reprocess = useMutation({
    mutationFn: (item: InvoiceReturn) => invoiceReturnsApi.reprocess(item.id),
    onSuccess: (result) => {
      if (result.resolved) toast.success("Chamada reprocessada e baixada.");
      else toast.warning(`Continua com pendência: ${invoiceReturnReasonLabel(result.reason)}`);
      queryClient.invalidateQueries({ queryKey: ["invoice-returns"] });
    },
    onError: (error) => toast.error(integratedOrderErrorMessage(error)),
  });

  const discard = useMutation({
    mutationFn: (item: InvoiceReturn) => invoiceReturnsApi.discard(item.id),
    onSuccess: () => {
      toast.success("Chamada descartada.");
      queryClient.invalidateQueries({ queryKey: ["invoice-returns"] });
    },
    onError: (error) => toast.error(integratedOrderErrorMessage(error)),
    onSettled: () => setToDiscard(null),
  });

  const pageCount = integratedOrderPageCount(filtered.length, pageSize);
  const currentPage = Math.min(page, pageCount);
  const items = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (!isInvoiceReturnsEnabled) return <Navigate to="/admin-pedidos" replace />;

  const summaryCards: { key: string; label: string; value: number; onClick: () => void; selected: boolean }[] = [
    {
      key: "all",
      label: "Total",
      value: summary.total,
      selected: kind === "all" && result === "all",
      onClick: () => { setKind("all"); setResult("all"); },
    },
    {
      key: "invoice",
      label: "Notas fiscais",
      value: summary.invoices,
      selected: kind === "invoice" && result === "all",
      onClick: () => { setKind("invoice"); setResult("all"); },
    },
    {
      key: "status",
      label: "Status da OV",
      value: summary.statuses,
      selected: kind === "status" && result === "all",
      onClick: () => { setKind("status"); setResult("all"); },
    },
    {
      key: "pending",
      label: "Com pendência",
      value: summary.pending,
      selected: kind === "all" && result === "pending",
      onClick: () => { setKind("all"); setResult("pending"); },
    },
  ];

  return (
    <Layout title="Retornos do ERP">
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Inbox className="h-5 w-5" />
            <p>
              Notas fiscais e status de OV que o ERP enviou ao portal, dos pedidos enviados direto ao ERP. O ERP sempre
              recebe confirmação; &quot;Com pendência&quot; é o que chegou mas não foi gravado por completo: a chamada fica
              guardada e pode ser corrigida, reprocessada ou descartada aqui. A tela atualiza sozinha a cada 30 segundos.
            </p>
          </div>
          <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {summaryCards.map((card) => (
            <button
              key={card.key}
              type="button"
              onClick={card.onClick}
              className={`rounded-lg border-2 p-4 text-left transition-colors ${card.selected
                ? "border-primary bg-primary/5"
                : "border-gray-300 bg-gray-50 hover:border-primary/50 hover:bg-primary/5"}`}
            >
              <p className="text-xs text-muted-foreground">{card.label}</p>
              <p className="mt-1 text-2xl font-bold">{isLoading ? "—" : card.value}</p>
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-3">
          <label className="relative min-w-[280px] flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Buscar escola, pedido, pré-pedido, nota fiscal ou OV"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <Select value={kind} onValueChange={(value) => setKind(value as InvoiceReturnKindFilter)}>
            <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {INVOICE_RETURN_KIND_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={result} onValueChange={(value) => setResult(value as InvoiceReturnResultFilter)}>
            <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {INVOICE_RETURN_RESULT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Card>
          <CardHeader className="border-b bg-muted/20">
            <CardTitle className="flex items-center justify-between text-base">
              Retornos do ERP
              <Badge variant="outline">{filtered.length} registros</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="space-y-3 p-6"><Skeleton className="h-10" /><Skeleton className="h-10" /><Skeleton className="h-10" /></div>
            ) : isError ? (
              <div className="p-8 text-center text-sm text-destructive">Não foi possível consultar os retornos do ERP.</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                {all.length === 0 ? "O ERP ainda não enviou nenhum retorno." : "Nenhum registro encontrado para os filtros."}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recebido em</TableHead>
                    <TableHead>Escola</TableHead>
                    <TableHead>Pedido</TableHead>
                    <TableHead>Pré-pedido</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Detalhe</TableHead>
                    <TableHead>Resultado</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => {
                    const detail = invoiceReturnDetail(item);
                    return (
                      <TableRow key={item.key}>
                        <TableCell className="whitespace-nowrap text-sm">{formatIntegratedOrderDate(item.receivedAt)}</TableCell>
                        <TableCell>
                          <div className="font-medium">{item.clientName || "Escola não identificada"}</div>
                          <div className="text-xs text-muted-foreground">{item.clientCode ?? "—"}</div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap font-medium">{item.orderNumber ?? "—"}</TableCell>
                        <TableCell>
                          <div className="font-medium">{item.preOrderId ?? "—"}</div>
                          <div className="text-xs text-muted-foreground">{item.preOrderReference ?? ""}</div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm">{invoiceReturnKindLabel(item.kind)}</TableCell>
                        <TableCell>
                          <div className="font-medium">{detail.title}</div>
                          {detail.subtitle && <div className="text-xs text-muted-foreground">{detail.subtitle}</div>}
                        </TableCell>
                        <TableCell>
                          <Badge variant={item.success ? "secondary" : "destructive"}>
                            {item.success ? "Recebido" : "Com pendência"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {item.success ? (
                            <span className="block text-right text-muted-foreground">—</span>
                          ) : (
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon" title="Ver detalhes" onClick={() => setDetails(item)}>
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" title="Corrigir" onClick={() => setEditing(item)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Reprocessar"
                                disabled={reprocess.isPending}
                                onClick={() => reprocess.mutate(item)}
                              >
                                <RefreshCw className={`h-4 w-4 ${reprocess.isPending && reprocess.variables?.id === item.id ? "animate-spin" : ""}`} />
                              </Button>
                              <Button variant="ghost" size="icon" title="Descartar" onClick={() => setToDiscard(item)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 border-t p-4 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                Itens por página
                <Select value={String(pageSize)} onValueChange={(value) => setPageSize(Number(value))}>
                  <SelectTrigger className="h-8 w-[80px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => <SelectItem key={size} value={String(size)}>{size}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span>Página {currentPage} de {pageCount}</span>
                <Button variant="outline" size="icon" disabled={currentPage >= pageCount} onClick={() => setPage(currentPage + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <InvoiceReturnDetailsDialog item={details} onClose={() => setDetails(null)} />
      <InvoiceReturnEditSheet item={editing} onClose={() => setEditing(null)} onSaved={() => refetch()} />

      <AlertDialog open={toDiscard != null} onOpenChange={(open) => !open && !discard.isPending && setToDiscard(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar a chamada {toDiscard?.id}?</AlertDialogTitle>
            <AlertDialogDescription>
              A chamada ({toDiscard ? invoiceReturnKindLabel(toDiscard.kind).toLowerCase() : ""}
              {toDiscard?.invoiceNumber ? ` ${toDiscard.invoiceNumber}` : ""}) deixa de aparecer como pendente e o corpo
              guardado, com os arquivos, é apagado. Isso não pode ser desfeito; para tratar depois, use Corrigir ou
              Reprocessar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={discard.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={discard.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (toDiscard) discard.mutate(toDiscard);
              }}
            >
              Descartar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
